"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const SuppliersContext = createContext();
export const useSuppliers = () => useContext(SuppliersContext);

export const SuppliersProvider = ({ children }) => {
  const { token, isReady, logout } = useAuth();

  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);

  const SUPPLIERS_ENDPOINT = "/suppliers";
  const baseURL = BASE_URL.replace(/\/$/, "");

  /* ---------------- MOUNT SAFETY ---------------- */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetSuppliers = (value) => {
    if (mountedRef.current) setSuppliers(value);
  };

  const safeSetError = (value) => {
    if (mountedRef.current) setError(value);
  };

  /* ---------------- FETCH ---------------- */
  const fetchSuppliers = useCallback(async () => {
    if (!token) return;

    setSuppliersLoading(true);
    safeSetError(null);

    try {
      const res = await fetch(`${baseURL}${SUPPLIERS_ENDPOINT}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const payload = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(payload?.message || "Fetch failed");

      safeSetSuppliers(Array.isArray(payload?.data) ? payload.data : []);

      return payload;
    } catch (err) {
      console.error("[Suppliers] fetch error:", err);

      safeSetError(err.message);

      if (err.message?.toLowerCase().includes("token")) {
        logout?.();
      }

      safeSetSuppliers([]);
      throw err;
    } finally {
      if (mountedRef.current) setSuppliersLoading(false);
    }
  }, [token, baseURL, logout]);

  /* ---------------- CREATE ---------------- */
const addSupplier = useCallback(
  async (formData) => {
    try {
      const res = await fetch(`${baseURL}${SUPPLIERS_ENDPOINT}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",   // ← ADD THIS
        },
        body: JSON.stringify(formData),          // ← ADD JSON.stringify
      });

      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload?.message || "Create failed");

      await fetchSuppliers();
      return payload;
    } catch (err) {
      console.error("[Suppliers] add error:", err);
      throw err;
    }
  },
  [token, baseURL, fetchSuppliers]
);

  /* ---------------- UPDATE ---------------- */
  const updateSupplier = useCallback(
    async (id, data) => {
      try {
        const res = await fetch(
          `${baseURL}${SUPPLIERS_ENDPOINT}/${id}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
          }
        );

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Update failed");

        await fetchSuppliers();
        return payload;
      } catch (err) {
        console.error("[Suppliers] update error:", err);
        throw err;
      }
    },
    [token, baseURL, fetchSuppliers]
  );

  /* ---------------- DELETE ---------------- */
  const deleteSupplier = useCallback(
    async (id) => {
      try {
        const res = await fetch(
          `${baseURL}${SUPPLIERS_ENDPOINT}/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Delete failed");

        safeSetSuppliers((prev) =>
          prev.filter((s) => String(s.id) !== String(id))
        );

        return payload;
      } catch (err) {
        console.error("[Suppliers] delete error:", err);
        throw err;
      }
    },
    [token, baseURL]
  );

  /* ---------------- STATUS (UNIFIED STYLE) ---------------- */
  const updateSupplierStatus = useCallback(
    async (id, status) => {
      try {
        const res = await fetch(
          `${baseURL}${SUPPLIERS_ENDPOINT}/${id}/status`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ status }),
          }
        );

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(payload?.message || "Status failed");

        safeSetSuppliers((prev) =>
          prev.map((s) =>
            String(s.id) === String(id)
              ? { ...s, status }
              : s
          )
        );

        return payload;
      } catch (err) {
        console.error("[Suppliers] status error:", err);
        throw err;
      }
    },
    [token, baseURL]
  );

  /* ---------------- INIT LOAD ---------------- */
  useEffect(() => {
    if (!isReady || !token) return;
    fetchSuppliers().catch(() => {});
  }, [isReady, token, fetchSuppliers]);

  return (
    <SuppliersContext.Provider
      value={{
        suppliers,
        suppliersLoading,
        error,
        fetchSuppliers,
        addSupplier,
        updateSupplier,
        deleteSupplier,
        updateSupplierStatus,
      }}
    >
      {children}
    </SuppliersContext.Provider>
  );
};